import { acceleratorModelSchema } from "./accelerator-table.js";
import { COMPUTE_CLASSES } from "./compute-class.js";
import { APP_POOL_REGION_PATTERN } from "./region-grammar.js";
import { K8S_QUANTITY_PATTERN, MAX_K8S_QUANTITY_LEN, compareScaledK8sQuantities, parseK8sQuantity } from "./fixture-resources.js";
import { blobBucketNameSchema, fixtureBlobStorageBindingSchema } from "./blob-buckets.js";
import { WORK_NAME_PATTERN, workNameSchema } from "./work-name.js";
import "./dp-sizing.js";
import { SCHEMA_NAME_PATTERN } from "./fixture-schema-derivation.js";
import { SCALING_INTENT_DISK_GIB_MAX } from "./scaling-intent.js";
import { z } from "zod";
//#region src/app-pool.ts
/**
* App pools: the app-level, named, queue-fed unit of confined compute (2026-09-04-app-pools).
*
* This module is the one declaration of the `pools`, `bindings` and `autoscalers` shapes an
* `app.json` may carry. It also holds the bounds every knob obeys and the pure helpers both publish
* doors and the operator use to compare compute vectors. Every other package imports from here; nothing
* re-declares a shape (`00-index.md` §1.1). The JSON Schema twin is
* `schemas/app-manifest.schema.json`; `app-pool.test.ts` pins the two byte-equal where a value is
* shared (the quantity grammar, the name grammar, the enums).
*
* The design and its sources: `requirements/architecture/plans/2026-09-04-app-pools/overview.md`
* §2 and §5 (L-3 to L-16), `design-docs/WS-1.md` §3.
*/
/** The discriminant of a pool (L-4, H14). `in-mesh` runs the app's own work with the app's identity
*  on a per-app packed node group; `out-of-mesh` runs one tenant's work with no platform credential
*  on a per-org node group. Each arm's fields are absent from the other arm's type. */
const APP_POOL_WORKLOADS = ["in-mesh", "out-of-mesh"];
/** Egress for an out-of-mesh pool (L-5, H12, H14). `closed` is a deny-all NetworkPolicy with no DNS;
*  `open` is `0.0.0.0/0` minus the reserved CIDRs plus cluster DNS. An allowlist member is a future
*  addition (overview O-4). The deprecated `WORK_TARGET_EGRESS_MODES` spells this `sealed` and is
*  untouched (I-1). */
const APP_POOL_EGRESS_MODES = ["closed", "open"];
/** Member lifetime (L-11). `per-attempt`: one pod per attempt, destroyed after. `standing`:
*  `concurrency` resident members, each serving one attempt at a time (I-6). */
const APP_POOL_LIFETIMES = ["per-attempt", "standing"];
/** Ordering (L-7). `per-tenant`: one in-flight attempt per org, order preserved per org, fairness
*  across orgs. `none`: no ordering guarantee, full parallelism up to `concurrency`. */
const APP_POOL_ORDERINGS = ["per-tenant", "none"];
/** `concurrency` 1..1000: the bounds `$defs/workTarget.concurrency` carries today (audit §6). */
const APP_POOL_CONCURRENCY_MIN = 1;
const APP_POOL_CONCURRENCY_MAX = 1e3;
/** `warmFloor` 0..50: the bounds `$defs/poolScalingIntent.warmFloor` and `$defs/workTarget.warmFloor`
*  share today (audit §3). */
const APP_POOL_WARM_FLOOR_MIN = 0;
const APP_POOL_WARM_FLOOR_MAX = 50;
/** `maxNodes` 1..`POOL_NODE_COUNT_MAX`: the platform ceiling every node-count field is bounded by
*  (`dp-sizing.ts`), the same bound `DpAdminLimits.maxNodesPerPool` itself has. */
const APP_POOL_MAX_NODES_MIN = 1;
const APP_POOL_MAX_NODES_MAX = 100;
/** `maxDurationSeconds` 10..7200. The lower bound is the one `$defs/pool` and `$defs/workTarget`
*  carry (audit §6). The upper bound was 3600 until S5; I-19 recorded that the zion-build port would
*  need more, and its longest gate is 2400 s with a 3600 s runner target, so 7200 gives that gate a
*  3x margin (R-072). `MAX_WORK_TIMEOUT_MS` in `work.ts` is the same figure at the submit body, and
*  `app-manifest.schema.json`'s two `maxDurationSeconds.maximum` twins are pinned to this constant by
*  `app-pool-schema.test.ts`. The deprecated `POOL_MAX_DURATION_SECONDS_MAX` stays at 3600. */
const APP_POOL_MAX_DURATION_SECONDS_MIN = 10;
const APP_POOL_MAX_DURATION_SECONDS_MAX = 7200;
/** `gracePeriodSeconds` 1..600: the bounds `$defs/pool.gracePeriodSeconds` carries today. */
const APP_POOL_GRACE_PERIOD_SECONDS_MIN = 1;
const APP_POOL_GRACE_PERIOD_SECONDS_MAX = 600;
/** `cooldownSeconds` 0..3600: 0 permits an immediate scale-down. The upper bound is its own literal
*  (R-072): a cooldown holds a warm node with no work on it, and an hour of that is the most the
*  platform bills an author for hesitation. It was defined as the duration cap until S5, which is how
*  raising the duration cap would have dragged it to 7200 unnoticed. */
const APP_POOL_COOLDOWN_SECONDS_MIN = 0;
const APP_POOL_COOLDOWN_SECONDS_MAX = 3600;
/** `image.entrypoint` 1..512: the cap `$defs/pool.entrypoint` and `$defs/workTarget.entrypoint`
*  carry, from issue #1753 (an unbounded app-authored string produced a 1,000,443-character 400). */
const APP_POOL_ENTRYPOINT_MAX_LENGTH = 512;
/** `bindings[].payload` serialized cap, 64 KiB. Lead: guess (I-17). The pipe cap is 1 MiB; a
*  manifest-embedded payload is configuration and this is the cap chosen for it. */
const APP_POOL_BINDING_PAYLOAD_MAX_BYTES = 65536;
/** `bindings[].on.schedule` 9..128 characters: the bounds `$defs/workTrigger.on.schedule` carries. */
const APP_POOL_SCHEDULE_MIN_LENGTH = 9;
const APP_POOL_SCHEDULE_MAX_LENGTH = 128;
/** An autoscaler name: the `autoscalers{}` key grammar `fixture.schema.json` uses today
*  (`^[a-z][a-z0-9-]*$`), capped at the Kubernetes label-value length. */
const APP_POOL_ALGORITHM_PATTERN = /^[a-z][a-z0-9-]*$/;
const APP_POOL_ALGORITHM_MAX_LENGTH = 63;
/** `on.event` topic: the length bound both twins carry. The grammar is not restated here, and both
*  twins carry only `minLength` and `maxLength`. `binding-event-topic-reserved` in
*  `app-pool-rules.ts` is the one grammar gate: it calls `assertValidTopic`, whose `TOPIC_PATTERN`
*  is module-private to `event-subjects.ts`, and it runs at both publish doors. A `pattern` on the
*  two twins would be a second declaration of that grammar. A byte-equality pin for it would have
*  to widen this package's public API to reach the private one. */
const APP_POOL_EVENT_TOPIC_MAX_LENGTH = 128;
/** The keys a compute vector may carry (L-10). GPUs come from `class`, never from a key
*  (`resourceQuantities.propertyNames` refuses `nvidia.com/gpu` by construction). */
const COMPUTE_VECTOR_KEYS = [
	"cpu",
	"memory",
	"ephemeral-storage"
];
/** The two GPU members of `COMPUTE_CLASSES`. Both pool arms accept them: the node group item carries
*  the accelerator for in-mesh and out-of-mesh work alike, so the out-of-mesh refusal O-9 recorded is
*  gone (2026-09-29-gpu-and-regional-app-pools D-20). A consumer that asks "does this class want a
*  GPU" reads this list. A test pins it to `COMPUTE_CLASSES` so a renamed class cannot leave a stale
*  literal here. */
const APP_POOL_GPU_CLASSES = ["gpu-inference", "gpu-training"];
/** Per-app caps (R-012, I-30). Lead: guess. The derived arrays are capped at 100 node groups per
*  data plane and 200 pool rows per report. One app past twenty pools would exhaust a data plane's
*  group budget on its own. */
const APP_POOL_MAX_POOLS_PER_APP = 20;
const APP_POOL_MAX_BINDINGS_PER_APP = 64;
const APP_POOL_MAX_AUTOSCALERS_PER_APP = 8;
/** A node-group id: `apg-` plus 20 hex. Declared once here. The cp-dp spec, the status schema and
*  the XRD mirror test import it (SEC-11). 20 hex is derived against GKE's 40-character node-pool
*  id budget, the same derivation `isolatedPoolId` carries. */
const APP_POOL_NODE_GROUP_ID_PATTERN = /^apg-[0-9a-f]{20}$/;
/** The one injection-guard grammar for a value the go-template or a label carries: lowercase
*  alphanumerics and hyphens, no leading or trailing hyphen. `appKey` in the node-group spec and
*  `nodeType` both use it (APL-2: one declaration, two readers). */
const APP_POOL_LABEL_VALUE_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
/** An alias by reference of `APP_POOL_LABEL_VALUE_PATTERN` (R-035). The name survives because three
*  schemas guard a field named `nodeType` with it, so a reader finds the constant under the field's
*  own name. */
const APP_POOL_NODE_TYPE_PATTERN = APP_POOL_LABEL_VALUE_PATTERN;
/** Upper bound on a `nodeType`. It is the same 40 the XRD pins on `provisionedNodeTypes[].nodeType`
*  and `MACHINE_TYPE_SLUG_PATTERN` carries. GCP gives a GKE nodePoolId 40 characters, the
*  derivation `APP_POOL_NODE_GROUP_ID_PATTERN` states above. The longest GCE machine-type name in
*  the tree is `x4-megamem-960-metal` at 20 characters, so half the budget is headroom. */
const APP_POOL_NODE_TYPE_MAX_LENGTH = 40;
/** A region name in `APP_POOL_REGION_PATTERN`'s grammar. Parse succeeds for `us-east4`; it fails for
*  `US-EAST4`, `4us`, `u` (too short), a 32-character name, and any non-string. Pure. */
const appPoolRegionSchema = z.string().regex(APP_POOL_REGION_PATTERN, "a region name such as us-east4");
/** An array whose members are distinct, the twin of JSON Schema `uniqueItems: true`. */
const uniqueArray = (item) => z.array(item).refine((xs) => new Set(xs).size === xs.length, { message: "items must be unique" });
/**
* The `.strict()` zod twin of `fixture.schema.json` `$defs/fixtureInfrastructure` (SEC-4, R-003).
* The JSON Schema for an in-mesh pool carries `infrastructure` as a plain object, so a door that
* compiles the app schema alone still compiles. This schema is what `checkAppPools` parses it with
* (`pool-infrastructure-invalid`) and what `appPoolInMeshSchema` binds. `app-pool.test.ts` pins its
* key set to the JSON node's `properties`.
*/
const fixtureInfrastructureSchema = z.object({
	database: z.object({
		ownSchema: z.string().regex(SCHEMA_NAME_PATTERN).optional(),
		readSchemas: z.array(z.string().regex(SCHEMA_NAME_PATTERN)).optional(),
		search: z.boolean().optional()
	}).strict().optional(),
	redis: z.boolean().optional(),
	coreApiAccess: z.boolean().optional(),
	artifactRegistryRead: z.boolean().optional(),
	multiOrgAgentHost: z.boolean().optional(),
	machineEnrollment: z.boolean().optional(),
	orgCrypto: z.boolean().optional(),
	audit: z.boolean().optional(),
	llm: z.boolean().optional(),
	eventBus: z.boolean().optional(),
	coordinationKv: uniqueArray(z.enum([
		"AGENT_LEASES",
		"event-server-leader",
		"s3-archiver-leader"
	])).optional(),
	consumesStreams: uniqueArray(z.string().regex(/^[a-z0-9-]{1,40}$/)).optional(),
	consumesAllStreams: z.boolean().optional(),
	blobStorage: fixtureBlobStorageBindingSchema.optional(),
	platformSecrets: z.record(z.string().regex(/^[A-Z][A-Z0-9_]*$/), z.object({
		byteLength: z.number().int().min(16).max(64),
		encoding: z.enum(["base64url", "hex"]),
		description: z.string().optional()
	}).strict()).optional()
}).strict();
/** A pool name: `workNameSchema`, the one grammar proven safe as a Kubernetes label value and a
*  NATS durable-name component at once (40 characters, no leading or trailing hyphen). */
const appPoolNameSchema = workNameSchema;
const APP_POOL_NAME_PATTERN = WORK_NAME_PATTERN;
/** One Kubernetes quantity string, 1..32 characters, in the grammar `K8S_QUANTITY_PATTERN` names. */
const k8sQuantitySchema = z.string().min(1).max(32).regex(K8S_QUANTITY_PATTERN, "a Kubernetes quantity such as 500m, 2, 4Gi or 20Gi");
const atLeastOneKey = (v) => Object.keys(v).length >= 1;
/** A compute vector: what one attempt (or one standing member) reserves and is capped at.
*  Requests equal limits (Guaranteed QoS, L-10). Every key optional, at least one present:
*  `{}` would read as "I set my compute" and do nothing. */
const computeVectorSchema = z.object({
	cpu: k8sQuantitySchema.optional(),
	memory: k8sQuantitySchema.optional(),
	"ephemeral-storage": k8sQuantitySchema.optional()
}).strict().refine(atLeastOneKey, { message: "a compute vector names at least one of cpu, memory, ephemeral-storage" });
/** A node vector: the minimum machine shape the pool wants (L-10). Both keys required; the platform
*  resolves it to the smallest SKU in `class` whose allocatable satisfies it. Never a SKU name. */
const nodeVectorSchema = z.object({
	cpu: k8sQuantitySchema,
	memory: k8sQuantitySchema
}).strict();
/** The pool's compute bounds (L-11): `default` applies to a submission naming nothing; `max` is the
*  ceiling a submission may not exceed (a Kubernetes LimitRange). Both optional; `{}` refused.
*  `max` absent means the resolved node's allocatable, decided at reconcile. */
const appPoolComputeSchema = z.object({
	default: computeVectorSchema.optional(),
	max: computeVectorSchema.optional()
}).strict().refine(atLeastOneKey, { message: "compute names at least one of default, max" });
/** A function-valued knob (L-6, I-5): `algorithm` names a key of `autoscalers{}`; `max` is the
*  static bound the platform clamps the function's output to. The knob's own platform bound
*  still applies (`pool-knob-max-out-of-range`). */
const appPoolFunctionKnobSchema = z.object({
	algorithm: z.string().min(1).max(63).regex(APP_POOL_ALGORITHM_PATTERN),
	max: z.number().int().min(0)
}).strict();
/** A knob is an integer or a function knob. The per-knob range is applied by `boundedKnobSchema`. */
const appPoolKnobSchema = z.union([z.number().int(), appPoolFunctionKnobSchema]);
/** Build the schema for one knob with its range: the integer arm is `min..max`; the function arm's
*  `max` is `min..max` too. Pure. */
function boundedKnobSchema(min, max) {
	return z.union([z.number().int().min(min).max(max), appPoolFunctionKnobSchema.refine((k) => k.max >= min && k.max <= max, {
		message: `max must be within ${min}..${max}`,
		path: ["max"]
	})]);
}
/** Type guard for the function arm. */
function isFunctionKnob(knob) {
	return typeof knob === "object" && knob !== null;
}
/** The pool's image names a fixture in the same bundle (L-14); publish resolves the fixture's image
*  to a digest. `entrypoint` is a command override on that image, bounded by #1753. */
const appPoolImageSchema = z.object({
	fixture: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/),
	entrypoint: z.string().min(1).max(512)
}).strict();
/** The clouds a tier names (D-15). Every tier names its cloud, because GCP and AWS family names
*  collide in text (`c3`, `c4`, `m3`, `m4`). On a data plane the operator keeps the tiers of that
*  data plane's cloud. The members are the clouds `MACHINE_TYPE_FAMILY_LADDERS` carries. Frozen:
*  the publish rules iterate it, so a consumer that mutates it gets a `TypeError` and changes no
*  rule. */
const APP_POOL_TIER_CLOUDS = Object.freeze(["gcp", "aws"]);
/** The capacity type of a tier (D-2): `spot` is reclaimable capacity at the cloud's spot rate,
*  `on-demand` is standard capacity. Frozen. */
const APP_POOL_TIER_CAPACITIES = Object.freeze(["spot", "on-demand"]);
/**
* The one spelling of the spot capacity type on a stored or wire row
* (2026-10-24-pool-machine-generations, D-35, D-36): the literal `spot`, a member of {@link APP_POOL_TIER_CAPACITIES}.
*
* Who reads it: the billing shapes of `billing.ts` (the rate row, the rate write input, and the
* app-pool arm of the scale events), and the node group item and live node pool row of
* `@zion/sdk-cp-dp-contracts` (WS-3b). Each binds the optional member `capacityType` to this
* schema.
*
* The rule: a present `capacityType` means spot capacity at the cloud's spot rate. An absent key
* means on-demand. A read view that is nullable reads `null` as on-demand too. So on-demand has
* one spelling, the absent key, and every row written before the field existed reads as
* on-demand. Parse succeeds for `'spot'` alone; `'on-demand'`, `'SPOT'`, `null` and any non-string
* fail with one issue. This copies the region rule of `pricingRateEntrySchema`, where absent is
* the home row.
*/
const appPoolSpotCapacitySchema = z.literal("spot");
/** The most tiers one pool declares (lead: guess, recorded in `WS-1a.md`). Each kept tier is one
*  node group, and a data plane holds at most 100 groups. Eight covers four tiers on each cloud. */
const APP_POOL_MAX_TIERS = 8;
/** The source of the tier family grammar. Each regex below is built from it, so the schema holds
*  its own instance. */
const TIER_FAMILY_SOURCE = "^[a-z][a-z0-9]*(-[a-z0-9]+)*$";
/** The grammar of a tier's `family`: lowercase, a letter first, hyphen-separated alphanumeric
*  parts. It admits every GCP family (`c4d`) and every AWS family form (`m7i-flex`, `p6-b200`).
*  A consumer reads it. {@link appPoolCpuTierSchema} validates with a private instance of the same
*  source, so a consumer that recompiles this object changes no schema. */
const APP_POOL_TIER_FAMILY_PATTERN = new RegExp(TIER_FAMILY_SOURCE);
/** The longest `family`. The longest family name in the tree is 8 characters (`m7i-flex`). */
const APP_POOL_TIER_FAMILY_MAX_LENGTH = 20;
/**
* A CPU tier: one machine family on one cloud, with a capacity type and a node cap (D-2, D-15).
*
* `family` passes the grammar here. Its membership in the cloud's family list is the publish rule
* `pool-tier-family-unknown`, because the list depends on `cloud`. `maxNodes` is the tier's total
* node cap across zones (D-25), an integer from `APP_POOL_MAX_NODES_MIN` to
* `APP_POOL_MAX_NODES_MAX`; a function knob is not admitted. Any other key is a parse error.
*/
const appPoolCpuTierSchema = z.object({
	cloud: z.enum(APP_POOL_TIER_CLOUDS),
	family: z.string().min(1).max(20).regex(new RegExp(TIER_FAMILY_SOURCE)),
	capacity: z.enum(APP_POOL_TIER_CAPACITIES),
	maxNodes: z.int().min(1).max(APP_POOL_MAX_NODES_MAX)
}).strict();
/**
* A GPU tier: one accelerator model on one cloud, with a GPU count per node, a capacity type and a
* node cap (D-10). `accelerator` is a member of `ACCELERATOR_MODELS`; the cloud's shape comes from
* `ACCELERATOR_TABLE`. `gpuCount` is an integer from 1 to `MAX_GPU_COUNT`; the publish rule
* `pool-tier-gpu-count-unavailable` refuses a count the cloud does not sell for the model. Any
* other key, `family` included, is a parse error.
*/
const appPoolGpuTierSchema = z.object({
	cloud: z.enum(APP_POOL_TIER_CLOUDS),
	accelerator: acceleratorModelSchema,
	gpuCount: z.int().min(1).max(8),
	capacity: z.enum(APP_POOL_TIER_CAPACITIES),
	maxNodes: z.int().min(1).max(APP_POOL_MAX_NODES_MAX)
}).strict();
/** A tier: a CPU tier or a GPU tier. A tier with both `family` and `accelerator`, or with
*  neither, matches no arm and is a parse error. */
const appPoolTierSchema = z.union([appPoolCpuTierSchema, appPoolGpuTierSchema]);
/** A pool's ordered tiers (D-2, D-8): 1 to `APP_POOL_MAX_TIERS` entries. The platform fills tier 1
*  first, then each later tier, in the declared order. */
const appPoolTiersSchema = z.array(appPoolTierSchema).min(1).max(8);
/** True when `tier` is a GPU tier. Pure. */
function isAppPoolGpuTier(tier) {
	return "accelerator" in tier;
}
/**
* The tier key (D-26): the text that names one tier of a pool, unique inside the pool.
*
* Who calls it: the publish rule `pool-tier-duplicate`, the node group id hash (S2, WS-2a), the
* cap override table and route of provisioning-svc (S1, WS-1c) and the override envelope member
* (S1, WS-1b). Each reads this function, and none spells the key itself.
*
* Output: `<cloud>.<family>.<capacity>` for a CPU tier (`gcp.c4d.spot`), and
* `<cloud>.<accelerator>x<gpuCount>.<capacity>` for a GPU tier (`gcp.h100x8.on-demand`). The cap
* is not part of the key: a changed cap is the same tier. The output always matches
* {@link APP_POOL_TIER_KEY_PATTERN}. Pure, never throws on a parsed tier.
*/
function appPoolTierKey(tier) {
	const middle = isAppPoolGpuTier(tier) ? `${tier.accelerator}x${tier.gpuCount}` : tier.family;
	return `${tier.cloud}.${middle}.${tier.capacity}`;
}
/** The source of the tier key grammar. Each regex below is built from it, so the schema holds
*  its own instance. */
const TIER_KEY_SOURCE = `^(?:${APP_POOL_TIER_CLOUDS.join("|")})\\.[a-z][a-z0-9]*(?:-[a-z0-9]+)*\\.(?:${APP_POOL_TIER_CAPACITIES.join("|")})$`;
/** The grammar of a tier key ({@link appPoolTierKey}). Safe as a URL path segment and a label
*  value. Built from the cloud and capacity lists, so a new member reaches it with no edit here.
*  A consumer reads it. {@link appPoolTierKeySchema} validates with a private instance of the same
*  source, so a consumer that recompiles this object changes no schema. */
const APP_POOL_TIER_KEY_PATTERN = new RegExp(TIER_KEY_SOURCE);
/** The longest tier key: `aws`, the longest family, `on-demand` and two dots. */
const APP_POOL_TIER_KEY_MAX_LENGTH = 34;
/** A tier key as an input value, such as a route parameter of the cap override (WS-1c). Parse
*  succeeds for `gcp.c4d.spot` and `aws.c7i.on-demand`; it fails for `gcp.c4d`, `GCP.c4d.spot`,
*  `azure.c4d.spot`, a key past {@link APP_POOL_TIER_KEY_MAX_LENGTH} and any non-string. */
const appPoolTierKeySchema = z.string().max(34).regex(new RegExp(TIER_KEY_SOURCE), "a tier key such as gcp.c4d.spot");
/**
* What the platform does when a spot node under a running attempt is reclaimed (D-12).
*
* - `auto`: the platform runs the attempt again on the same run id, up to `maxPreemptions` (D-31).
* - `app`: the run ends `failed` with reason `preempted`, and the app decides (D-32).
* - `none`: the run ends `failed` with reason `preempted` (D-33).
*
* Every policy also publishes the run-preempted notice (D-32, D-41).
*/
const APP_POOL_ON_PREEMPT_POLICIES = Object.freeze([
	"auto",
	"app",
	"none"
]);
/** The policy an absent `onPreempt` means: `none`, today's path (D-33). */
const APP_POOL_ON_PREEMPT_DEFAULT = "none";
/** `maxPreemptions` 1..10, default 3 (D-31): the bound on `auto` re-runs of one run. The minimum
*  1 because 0 under `auto` would mean "never re-run", which is `none`. The maximum 10 is a guess
*  recorded in `WS-1a.md`: past ten reclaims a run on spot is not converging. */
const APP_POOL_MAX_PREEMPTIONS_MIN = 1;
const APP_POOL_MAX_PREEMPTIONS_MAX = 10;
const APP_POOL_MAX_PREEMPTIONS_DEFAULT = 3;
const appPoolBaseShape = {
	image: appPoolImageSchema,
	/**
	* The compute class (L-10). Optional since 2026-10-24-pool-machine-generations (D-16): a pool
	* declares `class` or `tiers`, never both and never neither. The publish rules
	* `pool-class-or-tiers-required` and `pool-tiers-refuses-field` hold that, because the two
	* arms of the JSON Schema twin carry both keys.
	*/
	class: z.enum(COMPUTE_CLASSES).optional(),
	node: nodeVectorSchema,
	compute: appPoolComputeSchema.optional(),
	concurrency: boundedKnobSchema(1, APP_POOL_CONCURRENCY_MAX),
	warmFloor: boundedKnobSchema(0, 50),
	/**
	* The pool's node cap. Optional since 2026-10-24-pool-machine-generations (D-16): a pool with
	* `class` must declare it (`pool-max-nodes-required`), and a pool with `tiers` must not
	* (`pool-tiers-refuses-field`), because its maximum is the sum of its tier caps (D-25).
	*/
	maxNodes: boundedKnobSchema(1, APP_POOL_MAX_NODES_MAX).optional(),
	/**
	* The ordered tiers (D-2, D-8, D-15, D-16). Absent means the pool declares `class`. From stage
	* S2 (WS-2b) the operator places a pool by its tiers, and expands a class ladder into on-demand
	* tiers with no change to the node group id. Until a data plane runs that operator,
	* release-engine withholds an app version whose pool declares `tiers` from it, by the DP version
	* floor (D-19, WS-1d). The caps of one cloud's tiers add up to `APP_POOL_MAX_NODES_MAX` at most
	* (`pool-tiers-max-nodes-exceeded`).
	*/
	tiers: appPoolTiersSchema.optional(),
	/** The re-run policy on preemption (D-12). Absent means {@link APP_POOL_ON_PREEMPT_DEFAULT}.
	*  The operator reads it from stage S3 (WS-3c); the DP version floor (D-19, WS-1d) withholds an
	*  app version that declares it from an older data plane.
	*  The publish rule `pool-preempt-policy-requires-spot-tier` refuses it on a pool with no spot
	*  tier, where nothing is ever preempted. */
	onPreempt: z.enum(APP_POOL_ON_PREEMPT_POLICIES).optional(),
	/** The bound on `auto` re-runs (D-31), 1..10. Absent means
	*  {@link APP_POOL_MAX_PREEMPTIONS_DEFAULT}. `pool-max-preemptions-requires-auto` refuses it
	*  under any policy but `auto`. */
	maxPreemptions: z.int().min(1).max(10).optional(),
	cooldownSeconds: z.number().int().min(0).max(APP_POOL_COOLDOWN_SECONDS_MAX),
	maxDurationSeconds: z.number().int().min(10).max(APP_POOL_MAX_DURATION_SECONDS_MAX),
	gracePeriodSeconds: z.number().int().min(1).max(600),
	lifetime: z.enum(APP_POOL_LIFETIMES),
	ordering: z.enum(APP_POOL_ORDERINGS),
	/** The region the pool's attempts and standing members run in, when it is not the data plane's
	*  own (D-4, D-13). Absent means the data plane's home cluster, which is every pool before this
	*  field existed. Present, it names a satellite cluster the data plane admin has enabled. Both
	*  arms carry it, and the publish rule `pool-region-requires-out-of-mesh` refuses it on an in-mesh
	*  pool (D-6). Either lifetime may carry it: 2026-10-02-regional-pools-release-2 deleted the rule
	*  that refused it on a standing pool, because the satellite now reconciles standing members from
	*  the per-region desired-state slice (that feature's §2.1, D-7). */
	region: appPoolRegionSchema.optional(),
	/**
	* The boot-disk size of every node in the pool's node group, in GiB
	* (2026-10-02-regional-pools-release-2 §2.3, DF-1).
	*
	* Absent means the size the composition renders for an app-pool node group today, so a pool
	* without it keeps its node group id and its nodes (D-11, DF-6). Present, it is an integer from
	* `SCALING_INTENT_DISK_GIB_MIN` to `SCALING_INTENT_DISK_GIB_MAX`, the bounds the dedicated twin
	* `nodeShape.diskGiB` already carries (DF-2: the existing constant, not a new one). The operator
	* compiles it onto the node group item and `appPoolNodeGroupId` hashes it only when set. A boot
	* disk is a node-config field GKE does not change in place, so a changed value re-creates the
	* group. No publish rule reads it: the schema bound is the whole check. Both arms carry it.
	*/
	diskSizeGb: z.int().min(10).max(SCALING_INTENT_DISK_GIB_MAX).optional(),
	/**
	* The GPU count a `gpu-training` pool asks for, the fixture contract's spelling (D-9, DF-2).
	*
	* Valid values are the integers 1 to `MAX_GPU_COUNT`. A training machine type sells a fixed GPU
	* count, so the count selects the machine type, and `resolveAppPoolPlacement` passes it to
	* `resolveMachineType`. The publish rules hold the class coupling, never the shape (DF-3):
	* `pool-gpu-count-required` refuses a `gpu-training` pool without it, and
	* `pool-gpu-count-not-allowed` refuses it on every other class. Both arms carry it.
	*/
	gpuCount: z.int().min(1).max(8).optional()
};
/** The fields every pool carries, whatever its workload. `concurrency`, `warmFloor` and the three
*  second-valued fields are required: nothing about sizing has a default (H16, L-10). `class` and
*  `maxNodes`, or `tiers`, give the placement (D-16), and the publish rules hold that exactly one
*  form is present. `region` absent means the home cluster, `diskSizeGb` absent means the composed
*  default disk, and `gpuCount` absent is legal only off the `gpu-training` class. */
const appPoolBaseSchema = z.object(appPoolBaseShape).strict();
/** An in-mesh pool (L-4). `infrastructure` is the fixture capability block, parsed by
*  `fixtureInfrastructureSchema` (SEC-4); the JSON Schema arm is a plain object (R-003) and the
*  publish rule `pool-infrastructure-invalid` reports a failure. `egress`, `containerRuntime`,
*  `writableRootFS` and `meshMode` are parse errors here (I-28). */
const appPoolInMeshSchema = z.object({
	workload: z.literal("in-mesh"),
	infrastructure: fixtureInfrastructureSchema.optional(),
	...appPoolBaseShape
}).strict();
/**
* The bucket an out-of-mesh pool writes its attempt output to (2026-10-22-pool-attempt-blob-reads,
* WS-1, D4 and defaults rows D-2 and D-3): `{ buckets: { <logical>: 'readWrite' } }`.
*
* Why it exists: an attempt writes its output (the attempt root
* `<dpOrgId>/<appKey>/attempts/<attemptId>/`) through the platform blob proxy. With this key the
* operator sends that output to the per-org bucket of the named logical bucket. The app's own
* fixtures read it there. With no key the attempt keeps the legacy bucket.
*
* Valid input: exactly one entry. The key is a logical name in {@link blobBucketNameSchema}, and the
* value is the literal `readWrite`. Output: the same object. Refused, as a parse error:
* - an empty `buckets` map, or a map with two or more entries;
* - a value other than `readWrite`, `read` included: the attempt writes, so a narrower mode cannot
*   serve it;
* - a key outside the bucket name grammar;
* - a legacy `bucket` or `maxSizeGb` key, or any other key at either level (strict).
*
* The form is the named fixture form, narrowed: one entry and one mode. Three publish rules
* read the rest of the bundle:
* - `blob-bucket-binding-declared`: `app.json` `blobStorage.buckets[]` must declare the name;
* - `pool-blob-bucket-requires-org-scope`: that declaration must have `scope: 'org'`;
* - `pool-blob-bucket-exceeds-fixtures`: a fixture of the app that runs a workload must bind the
*   name with `readWrite`.
*
* A fourth publish rule reads the pool itself. `pool-blob-bucket-requires-home-per-attempt` refuses
* the key on a pool with `lifetime: 'standing'` or a `region`, because only a per-attempt pod in the
* home cluster gets a blob group (2026-10-22-pool-attempt-blob-reads, R-012).
*
* The JSON twin is `$defs/appPoolOutOfMesh/properties/blobStorage` in `app-manifest.schema.json`.
*
* Module-private on purpose: the type a consumer reads is `AppPoolOutOfMesh['blobStorage']`.
*/
const appPoolOutOfMeshBlobStorageSchema = z.object({ buckets: z.record(blobBucketNameSchema, z.literal("readWrite")).refine((buckets) => Object.keys(buckets).length === 1, { message: "bind exactly one bucket" }) }).strict();
/** An out-of-mesh pool (L-4, L-8). `egress` required. `containerRuntime` and `writableRootFS`
*  default to `false` in meaning when absent; the platform applies the CP's grant, never these
*  values (chain B). `infrastructure` is a parse error here.
*
*  `blobStorage` is optional (2026-10-22-pool-attempt-blob-reads). Absent, an attempt writes its
*  output to the legacy bucket, as every pool did before the key existed. Present, it names the one
*  app bucket that receives the attempt output; see `appPoolOutOfMeshBlobStorageSchema` above. The
*  key grants the attempt pod no credential: the blob proxy writes for it, as it does today. */
const appPoolOutOfMeshSchema = z.object({
	workload: z.literal("out-of-mesh"),
	egress: z.enum(APP_POOL_EGRESS_MODES),
	containerRuntime: z.boolean().optional(),
	writableRootFS: z.boolean().optional(),
	blobStorage: appPoolOutOfMeshBlobStorageSchema.optional(),
	...appPoolBaseShape
}).strict();
/** A pool: the discriminated union on `workload`. */
const appPoolSchema = z.discriminatedUnion("workload", [appPoolInMeshSchema, appPoolOutOfMeshSchema]);
/** `app.json` `pools`: an object keyed by pool name (L-3), at most `APP_POOL_MAX_POOLS_PER_APP`
*  entries (R-012). A keyed object gets name uniqueness for free, the `autoscalers{}` precedent.
*  The type is `AppPoolMap`; `AppPools` is the sdk-node client's name (DES-5). */
const appPoolsSchema = z.record(appPoolNameSchema, appPoolSchema).refine((m) => Object.keys(m).length <= 20, { message: `an app declares at most 20 pools` });
/** A schedule trigger: five-field Vixie cron, validated in full by `parseCronExpression` at the
*  publish doors (`binding-schedule-invalid`). The schema bounds the length only. */
const appPoolScheduleTriggerSchema = z.object({ schedule: z.string().min(9).max(128) }).strict();
/** An event trigger: an app event topic, validated by `assertValidTopic` at the publish doors
*  (`binding-event-topic-reserved`). */
const appPoolEventTriggerSchema = z.object({ event: z.string().min(1).max(128) }).strict();
/** Serialized JSON length of a value, in UTF-8 bytes. Pure. */
function serializedByteLength(value) {
	return new TextEncoder().encode(JSON.stringify(value)).length;
}
/** A producer outside the pool (L-13): on a schedule or an app event, the platform submits to
*  `pool` with `payload` (a JSON object at most `APP_POOL_BINDING_PAYLOAD_MAX_BYTES` serialized).
*  One of `schedule` or `event`, and never both, enforced by the union of two strict objects. */
const appPoolBindingSchema = z.object({
	pool: appPoolNameSchema,
	on: z.union([appPoolScheduleTriggerSchema, appPoolEventTriggerSchema]),
	payload: z.record(z.string(), z.unknown()).refine((p) => serializedByteLength(p) <= APP_POOL_BINDING_PAYLOAD_MAX_BYTES, { message: `payload serializes to more than ${APP_POOL_BINDING_PAYLOAD_MAX_BYTES} bytes` }).optional()
}).strict();
const appPoolBindingsSchema = z.array(appPoolBindingSchema).max(64);
/** The one shape a binding's submit hands the handler (I-25): the binding, its static payload (or
*  null), and either the schedule `occurrence` (ISO instant) or the verified `event`. */
const appPoolTriggerPayloadSchema = z.object({
	binding: appPoolBindingSchema,
	payload: z.record(z.string(), z.unknown()).nullable(),
	occurrence: z.string().datetime().optional(),
	event: z.unknown().optional()
}).strict();
/** Compare two Kubernetes quantities of one kind, exactly: two different quantities never compare
*  equal, also when both round to one float (`compareScaledK8sQuantities`). Throws `RangeError`
*  when either fails the grammar; the schemas refuse that earlier, so a throw here is a caller bug.
*  Pure. */
function compareQuantity(a, b) {
	if (parseK8sQuantity(a) === null) throw new RangeError(`not a Kubernetes quantity: ${a}`);
	if (parseK8sQuantity(b) === null) throw new RangeError(`not a Kubernetes quantity: ${b}`);
	return compareScaledK8sQuantities(a, 1, b, 1);
}
/** Does `ask` fit inside `ceiling`, key by key? A key present in `ask` and absent from `ceiling`
*  fails closed as `{ ok: false, key }`: no ceiling means no admission (SEC-3). Returns the first
*  key that exceeds. Pure. This is the check `admitAppPoolRun` runs at submit against the resolved
*  three-key ceiling and `checkAppPools` runs at publish (chain C hop 3, L-11). */
function computeFits(ask, ceiling) {
	for (const key of COMPUTE_VECTOR_KEYS) {
		const a = ask[key];
		if (a === void 0) continue;
		const c = ceiling[key];
		if (c === void 0) return {
			ok: false,
			key
		};
		if (compareQuantity(a, c) > 0) return {
			ok: false,
			key
		};
	}
	return { ok: true };
}
//#endregion
export { APP_POOL_ALGORITHM_MAX_LENGTH, APP_POOL_ALGORITHM_PATTERN, APP_POOL_BINDING_PAYLOAD_MAX_BYTES, APP_POOL_CONCURRENCY_MAX, APP_POOL_CONCURRENCY_MIN, APP_POOL_COOLDOWN_SECONDS_MAX, APP_POOL_COOLDOWN_SECONDS_MIN, APP_POOL_EGRESS_MODES, APP_POOL_ENTRYPOINT_MAX_LENGTH, APP_POOL_EVENT_TOPIC_MAX_LENGTH, APP_POOL_GPU_CLASSES, APP_POOL_GRACE_PERIOD_SECONDS_MAX, APP_POOL_GRACE_PERIOD_SECONDS_MIN, APP_POOL_LABEL_VALUE_PATTERN, APP_POOL_LIFETIMES, APP_POOL_MAX_AUTOSCALERS_PER_APP, APP_POOL_MAX_BINDINGS_PER_APP, APP_POOL_MAX_DURATION_SECONDS_MAX, APP_POOL_MAX_DURATION_SECONDS_MIN, APP_POOL_MAX_NODES_MAX, APP_POOL_MAX_NODES_MIN, APP_POOL_MAX_POOLS_PER_APP, APP_POOL_MAX_PREEMPTIONS_DEFAULT, APP_POOL_MAX_PREEMPTIONS_MAX, APP_POOL_MAX_PREEMPTIONS_MIN, APP_POOL_MAX_TIERS, APP_POOL_NAME_PATTERN, APP_POOL_NODE_GROUP_ID_PATTERN, APP_POOL_NODE_TYPE_MAX_LENGTH, APP_POOL_NODE_TYPE_PATTERN, APP_POOL_ON_PREEMPT_DEFAULT, APP_POOL_ON_PREEMPT_POLICIES, APP_POOL_ORDERINGS, APP_POOL_REGION_PATTERN, APP_POOL_SCHEDULE_MAX_LENGTH, APP_POOL_SCHEDULE_MIN_LENGTH, APP_POOL_TIER_CAPACITIES, APP_POOL_TIER_CLOUDS, APP_POOL_TIER_FAMILY_MAX_LENGTH, APP_POOL_TIER_FAMILY_PATTERN, APP_POOL_TIER_KEY_MAX_LENGTH, APP_POOL_TIER_KEY_PATTERN, APP_POOL_WARM_FLOOR_MAX, APP_POOL_WARM_FLOOR_MIN, APP_POOL_WORKLOADS, COMPUTE_VECTOR_KEYS, K8S_QUANTITY_PATTERN, MAX_K8S_QUANTITY_LEN, appPoolBaseSchema, appPoolBindingSchema, appPoolBindingsSchema, appPoolComputeSchema, appPoolCpuTierSchema, appPoolEventTriggerSchema, appPoolFunctionKnobSchema, appPoolGpuTierSchema, appPoolImageSchema, appPoolInMeshSchema, appPoolKnobSchema, appPoolNameSchema, appPoolOutOfMeshSchema, appPoolRegionSchema, appPoolScheduleTriggerSchema, appPoolSchema, appPoolSpotCapacitySchema, appPoolTierKey, appPoolTierKeySchema, appPoolTierSchema, appPoolTiersSchema, appPoolTriggerPayloadSchema, appPoolsSchema, boundedKnobSchema, compareQuantity, computeFits, computeVectorSchema, fixtureInfrastructureSchema, isAppPoolGpuTier, isFunctionKnob, k8sQuantitySchema, nodeVectorSchema, serializedByteLength };

//# sourceMappingURL=app-pool.js.map